import { useState, useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { TranslationContext } from 'contexts/translation-context';
import { useExecuteRecaptcha } from 'hooks/useExecuteRecaptcha';
import { Button } from 'components/common/buttons/button';
import { getErrorMessage, checkPromoCodeApplicability } from 'utils/promo-code-helper';
import { BlueLink } from 'components/common/link';
import type { PlanState } from '../reducers/plan';
import { applyPromoCode } from '../actions';
import { PROMO_CODE_STATUS } from 'types/join';
import { Row } from 'components/common/grid';

const PromoCodeContainer = styled.div<{ isActive?: boolean }>`
  margin-top: 15px;
  margin-bottom: 15px;
  opacity: ${props => (props.isActive ? 1 : 0.5)};
  pointer-events: ${props => (props.isActive ? 'auto' : 'none')};
`;

const PromoCodeLink = styled(BlueLink)`
  text-decoration: underline;
  font-size: 12px;
  margin-bottom: 10px;
  font-family: ${props => props.theme.fonts.montserratMedium};
`;

const PromoCodeInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
`;

const StyledButton = styled(Button)`
  &.promo-code-apply-button {
    background: none;
    border: none;
    font-size: 12px;
    color: ${props => props.theme.colors.siteBlue};
    height: 16px;
    &:active,
    &:focus,
    &:hover {
      background: none;
      border: none;
      box-shadow: none;
    }
  }
`;

const PromoCodeInput = styled.input`
  flex: 1;
  border: none;
  border-bottom: 1px solid #707070;
  padding: 5px 0;
  font-size: 12px;
  font-family: ${props => props.theme.fonts.montserratMedium};
  outline: none;

  &::placeholder {
    color: #707070;
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.red};
  font-size: 12px;
  margin-top: 5px;
  font-family: ${props => props.theme.fonts.montserratMedium};
`;

const NonApplicableMessage = styled.div`
  color: ${props => props.theme.colors.orange};
  font-size: 12px;
  margin-top: 5px;
  font-family: ${props => props.theme.fonts.montserratMedium};
`;

const SuccessMessage = styled.div`
  color: ${props => props.theme.colors.green};
  font-size: 12px;
  margin-top: 5px;
  font-family: ${props => props.theme.fonts.montserratMedium};
`;

const PromoCodeNameSuffix = styled.div`
  font-size: 10px;
  font-family: ${props => props.theme.fonts.montserratMedium};
  margin-left: 5px;
  margin-top: 2px;
`;

interface PromoCodeProps {
  onPromoCodeApplied: (promoCodeId: string) => void;
  selectedPlan?: any;
  selectedAddons?: any[];
  userCountry?: string;
  isActive?: boolean;
}

export const PromoCode: React.FC<PromoCodeProps> = ({
  onPromoCodeApplied,
  selectedPlan,
  selectedAddons,
  userCountry,
  isActive = true
}) => {
  const TRANSLATION_TEXT = useContext(TranslationContext) as { [key: string]: string };
  const { execute } = useExecuteRecaptcha();

  const [showInput, setShowInput] = useState(false);
  const [promoCode, setPromoCode] = useState('');

  const { promoCode: promoCodeState } = useSelector((state: { plan: PlanState }) => state.plan);

  const handlePromoCodeClick = () => {
    setShowInput(!showInput);
    if (!showInput) {
      setPromoCode('');
    }
  };

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) return;

    try {
      execute({
        callback: applyPromoCode,
        params: [promoCode.trim()]
      });
    } catch (error) {
      // console.error('Error applying promo code:', error);
    }
  };

  useEffect(() => {
    if (promoCodeState.data && !promoCodeState.loading) {
      // Only call onPromoCodeApplied if the promo code is valid and has a promoCode object
      if (promoCodeState.data.isValid && promoCodeState.data.promoCode) {
        onPromoCodeApplied(promoCodeState.data.promoCode.promoCodeId);
        setShowInput(false);
      }
    }
  }, [promoCodeState, onPromoCodeApplied]);

  // Calculate applicability and messages outside of return statement
  const getApplicabilityMessage = () => {
    if (!promoCodeState.data?.isValid || !promoCodeState.data?.promoCode) {
      return null;
    }

    const applicability = checkPromoCodeApplicability(
      promoCodeState.data.promoCode,
      selectedPlan,
      selectedAddons,
      userCountry
    );

    if (applicability.isApplicable) {
      return <SuccessMessage>{TRANSLATION_TEXT.promo_code_applied}</SuccessMessage>;
    } else {
      return <NonApplicableMessage>{applicability.reason}</NonApplicableMessage>;
    }
  };

  const getStatusMessage = () => {
    if (!promoCodeState.data || promoCodeState.data.isValid) {
      return null;
    }

    // Use the getErrorMessage function to handle status-based responses
    const errorMessage = getErrorMessage(promoCodeState.data, TRANSLATION_TEXT.promo_code_error);
    return <ErrorMessage>{errorMessage}</ErrorMessage>;
  };

  const getPromoCodeNameSuffix = useMemo(() => {
    if (promoCodeState.data && promoCodeState.data.isValid && promoCodeState.data.promoCode) {
      return ` (${promoCodeState.data.promoCode.promoCodeName})`;
    }
    return '';
  }, [promoCodeState]);

  return (
    <PromoCodeContainer isActive={isActive}>
      {!showInput && (
        <Row>
          <PromoCodeLink onClick={handlePromoCodeClick} disabled={!isActive}>
            {promoCodeState.data?.isValid
              ? TRANSLATION_TEXT.change_promo_code || 'Change promo code'
              : TRANSLATION_TEXT.have_promo_code}
          </PromoCodeLink>
          <PromoCodeNameSuffix>{getPromoCodeNameSuffix}</PromoCodeNameSuffix>
        </Row>
      )}

      {showInput && (
        <PromoCodeInputContainer>
          <PromoCodeInput
            type="text"
            placeholder={TRANSLATION_TEXT.enter_promo_code}
            value={promoCode}
            onChange={e => setPromoCode(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && handleApplyPromoCode()}
            disabled={promoCodeState.loading}
          />
          <StyledButton
            className="promo-code-apply-button"
            onClick={handleApplyPromoCode}
            disabled={!promoCode.trim()}
            ids={['PROMO_CODE_DISCOUNT']}
          >
            {TRANSLATION_TEXT.apply}
          </StyledButton>
        </PromoCodeInputContainer>
      )}

      {promoCodeState.error && (
        <ErrorMessage>
          {getErrorMessage(promoCodeState.error, TRANSLATION_TEXT.promo_code_error)}
        </ErrorMessage>
      )}

      {getApplicabilityMessage()}
      {getStatusMessage()}
    </PromoCodeContainer>
  );
};
